import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { styled } from "styled-components";
import CloseIcon from "../../../asset/images/new/Close.svg";
import TractorService from "../../../service/tractor/tractorApi";
import Pagination from "react-js-pagination";
import { formatWonToEokMan } from "../function";
import { AnalysisForm, TractorItem } from "../machine";

const brandList = [
  "엘에스엠트론㈜",
  "대동공업(주)",
  "국제종합기계㈜",
  "(주)아세아텍",
  "(주)명성",
  "(주)신한월드",
  "(주)에이치에스엠",
  "(주)한서정공",
  "동양물산기업㈜",
  "로보드림㈜",
  "얀마농기코리아㈜",
  "한국구보다㈜",
  "한국페라리트랙터",
];

const sortList = [
  {
    label: "마력 높은순",
    value: "ps_desc",
  },
  {
    label: "마력 낮은순",
    value: "ps_asc",
  },
  {
    label: "가격 높은순",
    value: "price_desc",
  },
  {
    label: "가격 낮은순",
    value: "price_asc",
  },
];

// const sortList = ["가격 높은순", "가격 낮은순", "마력 높은순", "마력 낮은순 "];

interface Props {
  formData: AnalysisForm;
  setFormData: Dispatch<SetStateAction<AnalysisForm>>;
  onClose: () => void;
}

function TractorPickerModal({ formData, setFormData, onClose }: Props) {
  const [tractorList, setTractorList] = useState<TractorItem[]>([]);
  const [selectBrands, setSelectBrands] = useState<string[]>([]);
  const [searchInput, setSearchInput] = useState("");
  const [openSortDropdown, setOpenSortDropdown] = useState(false);
  const [sort, setSort] = useState({
    label: "마력 높은순",
    value: "ps_desc",
  });
  const sortRef = useRef<HTMLDivElement | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const getList = async () => {
    try {
      const res = await TractorService.list(
        selectBrands.join(","),
        // "대동공업(주)",
        searchInput,
        10,
        currentPage,
        sort.value
      );
      setTractorList(res.list);
      setTotalCount(res.totalCount);
    } catch (error) {}
  };

  useEffect(() => {
    if (currentPage === 1) {
      getList();
    } else {
      setCurrentPage(1);
    }
  }, [sort, selectBrands]);

  useEffect(() => {
    getList();
  }, [currentPage]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    if (!openSortDropdown) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (!sortRef.current) return;
      if (!sortRef.current.contains(e.target as Node)) {
        setOpenSortDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openSortDropdown]);

  return createPortal(
    <Dimmer onClick={onClose} aria-modal role="dialog">
      <Dialog onClick={(e) => e.stopPropagation()}>
        <Header>
          <img
            src="/asset/images/arrow/backArrowTopNav.svg"
            alt=""
            className="mobile-only"
            onClick={onClose}
          />
          <Title>트랙터 추가하기</Title>
          <img
            src={CloseIcon}
            alt=""
            className="w-24 hover"
            onClick={onClose}
          />
        </Header>

        <div className="mobile-only">
          <Search>
            <InputWrapper>
              <img src="/asset/images/search/SearchGray20.svg" alt="" />
              <Input
                value={searchInput}
                placeholder="제조사, 모델명 검색"
                onChange={(e) => setSearchInput(e.target.value)}
                onBlur={() => {
                  if (currentPage === 1) {
                    getList();
                  } else {
                    setCurrentPage(1);
                  }
                }}
              />
            </InputWrapper>
          </Search>
          <TabContainer>
            <Tab
              $active={selectBrands.length === 0}
              onClick={() => setSelectBrands([])}
            >
              전체
            </Tab>
            {brandList.map((b) => (
              <Tab
                key={b}
                $active={selectBrands.includes(b)}
                $isLS={b === "엘에스엠트론㈜"}
                onClick={() => {
                  if (selectBrands.includes(b)) {
                    setSelectBrands((prev) => prev.filter((i) => i !== b));
                  } else {
                    setSelectBrands((prev) => [...prev, b]);
                  }
                }}
              >
                {b}
              </Tab>
            ))}
          </TabContainer>
        </div>

        <Body>
          <LeftSide className="desktop-only">
            <Search>
              <InputWrapper>
                <img src="/asset/images/search/SearchGray20.svg" alt="" />
                <Input
                  value={searchInput}
                  placeholder="제조사, 모델명 검색"
                  onChange={(e) => setSearchInput(e.target.value)}
                  onBlur={() => {
                    if (currentPage === 1) {
                      getList();
                    } else {
                      setCurrentPage(1);
                    }
                  }}
                />
              </InputWrapper>
            </Search>
            <LeftListContainer>
              <CheckItem onClick={() => setSelectBrands([])}>
                <img
                  src={`/asset/images/check/${selectBrands.length === 0 ? "Check24Green" : "Check24Gray"}.svg`}
                  alt=""
                />
                전체
              </CheckItem>
              {brandList.map((b) => (
                <CheckItem
                  key={b}
                  $isLS={b === "엘에스엠트론㈜"}
                  onClick={() => {
                    if (selectBrands.includes(b)) {
                      setSelectBrands((prev) => prev.filter((i) => i !== b));
                    } else {
                      setSelectBrands((prev) => [...prev, b]);
                    }
                  }}
                >
                  <img
                    src={`/asset/images/check/${selectBrands.includes(b) ? "Check24Green" : "Check24Gray"}.svg`}
                    alt=""
                  />
                  {b}
                </CheckItem>
              ))}
            </LeftListContainer>
          </LeftSide>
          <SearchResult>
            <ResultHeader>
              <ResultCnt>{totalCount}개</ResultCnt>
              <SortButtonContainer ref={sortRef}>
                <SortButton onClick={() => setOpenSortDropdown(true)}>
                  {sort.label}
                  <img
                    src="/asset/images/arrow/arrow_left_calendar_black.svg"
                    alt=""
                  />
                </SortButton>
                {openSortDropdown && (
                  <DropDown>
                    {sortList.map((s) => {
                      const active = s.value === sort.value;
                      return (
                        <DropdownItem
                          key={s.value}
                          onClick={() => {
                            setOpenSortDropdown(false);
                            setSort(s);
                          }}
                          $active={active}
                        >
                          <DropdownItemLabel>{s.label}</DropdownItemLabel>
                          {active && (
                            <img
                              src="/asset/images/check/Check24Green2.svg"
                              alt=""
                            />
                          )}
                        </DropdownItem>
                      );
                    })}
                  </DropDown>
                )}
              </SortButtonContainer>
            </ResultHeader>
            <ResultList>
              {tractorList.length === 0 && (
                <NoData>조건에 맞는 트랙터가 없습니다.</NoData>
              )}
              {tractorList.length > 0 &&
                tractorList.map((item) => {
                  const selected = formData.tractorList.find(
                    (selected) => selected.id === item.id
                  );
                  const isActive = !!selected;
                  const count = selected?.cnt ?? 1;
                  return (
                    <Item
                      key={item.id}
                      $active={isActive}
                      onClick={() => {
                        if (isActive) {
                          // 선택 해제: tractorList에서 제거
                          setFormData((prev) => ({
                            ...prev,
                            tractorList: prev.tractorList.filter(
                              (i) => i.id !== item.id
                            ),
                          }));
                        } else {
                          // 선택: tractorList에 cnt=1로 추가
                          setFormData((prev) => ({
                            ...prev,
                            tractorList: [
                              ...prev.tractorList,
                              { ...item, cnt: 1 },
                            ],
                          }));
                        }
                      }}
                    >
                      {isActive && (
                        <CheckIcon src="/asset/images/check/Check24Green2.svg" />
                      )}
                      <ItemInner>
                        <ItemTop>
                          <div className="brand">{item.brand}</div>
                          <div className="name">{item.model}</div>
                        </ItemTop>
                        <ItemContent>
                          <ContentItem>
                            <div className="label">마력</div>
                            <div className="value">{item.engine_ps}PS</div>
                          </ContentItem>
                          <ContentItem>
                            <div className="label">가격</div>
                            <div className="value">
                              {formatWonToEokMan(item.price)}
                            </div>
                          </ContentItem>
                          <ContentItem>
                            <div className="label">PTO</div>
                            <div className="value">{item.pto_ps}PS</div>
                          </ContentItem>
                          <ContentItem>
                            <div className="label">융자</div>
                            <div className="value">
                              {formatWonToEokMan(item.max_support_fund)}
                            </div>
                          </ContentItem>
                        </ItemContent>
                      </ItemInner>
                      {isActive && (
                        <ItemCntContainer
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                        >
                          <ChanceCntButton
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setFormData((prev) => ({
                                ...prev,
                                tractorList: prev.tractorList.map((t) =>
                                  t.id === item.id
                                    ? {
                                        ...t,
                                        cnt: Math.max(1, (t.cnt ?? 1) - 1), // 최소 1대 유지
                                      }
                                    : t
                                ),
                              }));
                            }}
                          >
                            <img src="/asset/images/icon/minus.svg" alt="" />
                          </ChanceCntButton>
                          <ItemCnt>{count}대</ItemCnt>
                          <ChanceCntButton
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setFormData((prev) => ({
                                ...prev,
                                tractorList: prev.tractorList.map((t) =>
                                  t.id === item.id
                                    ? {
                                        ...t,
                                        cnt: (t.cnt ?? 1) + 1,
                                      }
                                    : t
                                ),
                              }));
                            }}
                          >
                            <img src="/asset/images/icon/plus.svg" alt="" />
                          </ChanceCntButton>
                        </ItemCntContainer>
                      )}
                    </Item>
                  );
                })}

              {/* 페이지네이션 */}
              {totalCount > 10 && (
                <PaginationWrapper>
                  <Pagination
                    totalItemsCount={totalCount}
                    itemsCountPerPage={10}
                    pageRangeDisplayed={5}
                    activePage={currentPage}
                    onChange={(page) => {
                      setCurrentPage(page);
                    }}
                    firstPageText={
                      <img src="/asset/images/arrow/double_arrow_left.svg" />
                    }
                    lastPageText={
                      <img src="/asset/images/arrow/double_arrow_right_black.svg" />
                    }
                    prevPageText={
                      <img src="/asset/images/arrow/arrow_left_calendar_black.svg" />
                    }
                    nextPageText={
                      <img src="/asset/images/arrow/arrow_right_calendar_black.svg" />
                    }
                    innerClass="pagination" // ul
                    itemClass="page-item" // li
                    linkClass="page-link" // a
                    activeClass="active"
                    disabledClass="disabled"
                  />
                </PaginationWrapper>
              )}
            </ResultList>

            <Footer>
              <ResetButton
                onClick={() => {
                  setFormData((prev) => ({
                    ...prev,
                    tractorList: [],
                  }));
                }}
              >
                <img src="/asset/images/icon/reset.svg" alt="" />
                초기화
              </ResetButton>
              <ConfirmButton
                disabled={formData.tractorList.length === 0}
                onClick={onClose}
              >
                선택완료
              </ConfirmButton>
            </Footer>
          </SearchResult>
        </Body>
      </Dialog>
    </Dimmer>,
    document.body
  );
}
export default TractorPickerModal;

const Dimmer = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;

  .desktop-only {
    @media (max-width: 1024px) {
      display: none;
    }
  }

  .mobile-only {
    @media (min-width: 1024px) {
      display: none;
    }
  }
`;

const Dialog = styled.div`
  width: 640px;
  max-width: calc(100% - 40px);
  height: 80vh;
  background: #fff;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 1023px) {
    width: 100%;
    height: 100%;
    max-height: none;
    max-width: none;
    border-radius: 0;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 16px;
  background: #fff;
  border-bottom: 1px solid rgba(23, 30, 38, 0.1);

  @media (max-width: 1023px) {
    border: none;
  }
`;

const Title = styled.div`
  flex: 1 0 0;
  font-weight: 700;
  font-size: 16px;
  color: #171e26;

  @media (max-width: 1023px) {
    font-weight: 500;
    text-align: center;
  }
`;

const TabContainer = styled.div`
  width: 100%;
  overflow-x: auto;

  display: flex;
  padding: 12px 16px;
  align-items: flex-start;
  gap: 8px;
  border-bottom: 1px solid
    var(--Alias-Color-Line-Outline, rgba(23, 30, 38, 0.1));
  background: #fff;
`;

const Tab = styled.div<{ $active?: boolean; $isLS?: boolean }>`
  flex-shrink: 0;
  display: flex;
  height: 32px;
  padding: 8px 12px;
  justify-content: center;
  align-items: center;
  border-radius: 100px;
  background: var(--Alias-Color-Text-White-Gray-LightGray-200, #f0f0f0);
  color: var(--Alias-Color-Text-Dark-Text-Tertiary, rgba(23, 30, 38, 0.56));

  /* Spoqa/Body/13_Medium */
  font-family: "Spoqa Han Sans Neo";
  font-size: 13px;
  font-style: normal;
  font-weight: 500;
  line-height: 145%; /* 18.85px */
  letter-spacing: -0.26px;

  ${({ $isLS }) =>
    $isLS &&
    `
      font-weight: 700;
      color: rgba(23, 30, 38, 0.9);
    `}
  ${({ $active }) =>
    $active &&
    `
  background: #1E9B6B;
  color: #fff;
  `}
`;

const Body = styled.div`
  display: flex;
  align-items: flex-start;
  flex: 1 0 0;
  align-self: stretch;
  min-height: 0;
`;

const LeftSide = styled.div`
  display: flex;
  width: 240px;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  border-right: 1px solid var(--Alias-Color-Line-Outline, rgba(23, 30, 38, 0.1));
`;

const Search = styled.div`
  display: flex;
  padding: 8px;
  align-items: flex-start;
  gap: 10px;
  align-self: stretch;
  width: 100%;
`;

const InputWrapper = styled.div`
  width: 100%;
  display: flex;
  padding: 10px 12px;
  align-items: center;
  gap: 8px;
  border-radius: 8px;
  border: 1px solid var(--Alias-Color-Line-Outline, rgba(23, 30, 38, 0.1));
  background: var(--Alias-Color-Text-White-Gray-White, #fff);
`;

const Input = styled.input`
  min-width: 0;
  flex: 1 0 0;

  outline: none;
  border: none;
  background-color: transparent;
  color: var(--Alias-Color-Text-Dark-Text-Primary, #171e26);
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%; /* 24px */

  &::placeholder {
    color: var(--Alias-Color-Text-Text-Tertiary, rgba(23, 30, 38, 0.56));
  }
`;

const LeftListContainer = styled.div`
  flex: 1 1 auto;
  display: flex;
  padding: 12px 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  align-self: stretch;
  overflow-y: auto;
`;

const CheckItem = styled.div<{ $isLS?: boolean }>`
  display: flex;
  padding: 8px 0;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  cursor: pointer;
  color: var(--Alias-Color-Text-Dark-Text-Secondary, rgba(23, 30, 38, 0.78));

  /* Spoqa/Body/13_Medium */
  font-family: "Spoqa Han Sans Neo";
  font-size: 13px;
  font-style: normal;
  font-weight: 500;
  line-height: 145%; /* 18.85px */
  letter-spacing: -0.26px;

  ${({ $isLS }) =>
    $isLS &&
    `
    font-weight: 700;
    color: rgba(23, 30, 38, 0.9)
  `}
`;

const SearchResult = styled.div`
  flex: 1 0 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
`;

const ResultHeader = styled.div`
  width: 100%;
  display: flex;
  padding: 10px 16px;
  justify-content: space-between;
  align-items: center;
`;

const ResultCnt = styled.div`
  color: var(--Alias-Color-Text-Dark-Text-Primary, #171e26);
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%; /* 24px */
`;

const SortButtonContainer = styled.div`
  position: relative;
`;

const SortButton = styled.button`
  border: none;
  outline: none;
  cursor: pointer;

  display: flex;
  padding: 8px 16px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-radius: 100px;
  background: var(--Alias-Color-Text-White-Gray-LightGray-200, #f0f0f0);
  color: var(--Alias-Color-Text-Dark-Text-Primary, #171e26);
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 14px; /* 116.667% */

  & > img {
    width: 18px;
    height: 18px;
    transform: rotate(-90deg);
  }
`;

const DropDown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 100;

  display: flex;
  width: 160px;
  padding: 4px;
  flex-direction: column;
  align-items: flex-start;
  border-radius: 16px;
  border: 1px solid var(--Alias-Color-Line-Outline, rgba(23, 30, 38, 0.1));
  background: var(--Alias-Color-Background-Seciton, #fff);

  /* shadow/light/+Y/02 */
  box-shadow:
    0 4px 12px 0 rgba(39, 44, 54, 0.05),
    0 2px 4px 0 rgba(39, 44, 54, 0.12),
    0 8px 32px 0 rgba(39, 44, 54, 0.08);
`;

const DropdownItem = styled.div<{ $active?: boolean }>`
  display: flex;
  padding: 8px 12px;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  cursor: pointer;
  color: var(--Alias-Color-Text-Dark-Text-Tertiary, rgba(23, 30, 38, 0.56));

  /* Mo/Body/S-14-Medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px; /* 142.857% */

  ${({ $active }) => $active && "color: #171E26;"}

  & > img {
    width: 20px;
    height: 20px;
    aspect-ratio: 1/1;
  }
`;

const DropdownItemLabel = styled.div`
  flex: 1 0 0;
`;

const ResultList = styled.div`
  display: flex;
  padding: 8px 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  flex: 1 0 0;
  align-self: stretch;
  overflow-y: auto;
`;

const NoData = styled.div`
  display: flex;
  padding: 12px 0;
  justify-content: center;
  align-items: center;
  gap: 10px;
  align-self: stretch;
  color: var(--Alias-Color-Text-Dark-Text-Secondary, rgba(23, 30, 38, 0.78));
  text-align: center;
  font-feature-settings:
    "liga" off,
    "clig" off;

  /* Mo/Body/S-14-Medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px; /* 142.857% */
`;

const Item = styled.div<{ $active?: boolean }>`
  position: relative;
  cursor: pointer;
  display: flex;
  padding: 12px;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  align-self: stretch;
  border-radius: 8px;
  background: var(--Alias-Color-Text-White-Gray-LightGray-100, #f7f8f9);

  color: #171e26;
  font-size: 14px;
  font-weight: 500;

  ${({ $active }) =>
    $active &&
    `
  background: #EAF8F2;

  `}

  .brand {
    color: rgba(23, 30, 38, 0.78);
  }

  .name {
    font-size: 16px;
  }

  .label {
    width: 32px;
    color: rgba(23, 30, 38, 0.56);
  }

  .value {
    font-weight: 700;
  }
`;

const ItemCntContainer = styled.div`
  display: flex;
  padding: 4px;
  align-items: center;
  gap: 4px;
  align-self: stretch;
  border-radius: 8px;
  background: var(--Alias-Color-Text-White-Gray-White, #fff);
`;

const ChanceCntButton = styled.button`
  border: none;
  cursor: pointer;

  display: flex;
  width: 40px;
  height: 40px;
  padding: 8px;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  background: var(--Alias-Color-Text-White-Gray-LightGray-200, #f0f0f0);
`;
const ItemCnt = styled.div`
  display: flex;
  padding: 8px;
  justify-content: center;
  align-items: center;
  flex: 1 0 0;
  color: var(--Alias-Color-Text-Dark-Text-Secondary, rgba(23, 30, 38, 0.78));
  text-align: center;
  font-feature-settings:
    "liga" off,
    "clig" off;

  /* Mo/Body/L-16-Medium */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%; /* 24px */
`;

const ItemInner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  flex: 1 0 0;
  align-self: stretch;
`;

const ItemTop = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  flex: 1 0 0;
`;

const ItemContent = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px 0px;
  align-self: stretch;
`;

const ContentItem = styled.div`
  display: flex;
  padding: 4px 0;
  align-items: flex-start;
  align-self: stretch;
`;

const Footer = styled.div`
  width: 100%;
  padding: 16px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  border-top: 1px solid var(--Alias-Color-Line-Outline, rgba(23, 30, 38, 0.1));
  background: #fff;
`;
const ResetButton = styled.button`
  border: 0;
  cursor: pointer;

  display: flex;
  width: 120px;
  padding: 14px 16px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-radius: 8px;
  background: var(--Alias-Color-Text-White-Gray-White, #fff);
  color: var(--Alias-Color-Text-Dark-Text-Secondary, rgba(23, 30, 38, 0.78));
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%; /* 24px */
`;

const ConfirmButton = styled.button`
  border: 0;
  cursor: pointer;

  display: flex;
  width: 200px;
  padding: 14px 16px;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  background: #1e9b6b;
  color: #fff;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%; /* 24px */

  &:disabled {
    background: var(--Alias-Color-Text-White-Gray-LightGray-200, #f0f0f0);
    color: var(--Alias-Color-Text-Dark-Text-Disabled, rgba(23, 30, 38, 0.4));
  }
`;

const PaginationWrapper = styled.div`
  width: 100%;
  padding: 16px 0 32px;
  display: flex;
  justify-content: center;
  align-items: center;

  .pagination {
    display: flex;
    gap: 0;
    list-style: none;
    padding: 0;
    margin: 0;
    width: 100%;
  }

  .page-item {
    display: flex;
    flex: 1 0 0;
  }

  .page-link {
    min-width: 36px;
    height: 36px;
    padding: 0 8px;
    border-radius: 999px;
    border: none;
    background: transparent;
    display: flex;
    align-items: center;
    justify-content: center;

    font-size: 14px;
    font-weight: 500;
    color: rgba(23, 30, 38, 0.56);
    cursor: pointer;
  }

  .page-item.active .page-link {
    background-color: #eaf8f2; /* 연초록 배경 */
    color: #1e9b6b; /* 초록 글씨 */
    border: none;
  }

  .page-item.disabled .page-link {
    color: rgba(23, 30, 38, 0.3);
    cursor: default;
  }

  .pagination li.active {
    background: none;
  }
`;

const CheckIcon = styled.img`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 24px;
  height: 24px;
`;
